import { useEffect, useState } from "react";
import "../styles/ProductsPage.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "../redux/productsSlice";
import { Expand, Heart } from "lucide-react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { addToWishlist, removeFromWishlist, } from "../../../../components/Wishlist/WishlistSlice";
import { Slide, ToastContainer, toast } from "react-toastify";
import LoginPromptModal from "../../../../components/LoginModal/LoginPromptModal";
import Footer from "../../../../components/Footer/Footer";
import Pagination from "../../../../components/Pagination/Pagination";
import emptyproduct from "../../../../assets/images/empty-product.png";
import { useCartWishlist } from "../../../../app/CartWishlistContext";
import TopPickSection from "../../topPicks/pages/topPickPage";
import CustomerFavourite from "../components/CustomerFavourite";
import { getPaginationMeta } from "../../../../utils/pagination.utils";
import EmptyProducts from "../components/EmptyProducts";
import SkeletonProduct from "../components/skeletons/SkeletonProduct";
import {  capitalizeWords, limitChars, capitalizeWordsDash, capitalizeWordsRemoveDash, orderByString, orderByField } from "../../../../utils/text.utils";
import { formatDiscount, formatPrice } from "../../../../utils/price.utils";
import { getProductPdpUrl } from "../../../../utils/url.utils";
import SkeletonFilter from "../components/skeletons/SkeletonFilter";
import ProductBreadcrumbs from "../../../../components/Breadcrumbs/ProductBreadcrumbs";
import MobileFilter from "../components/MobileFilters";
import ProductQuickViewModal from "../../../quickView/pages/ProductsQuickViewModal";

const ProductsPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { getCartWishlistCount } = useCartWishlist();
    const { categorySlug, subcategorySlug } = useParams();
    const category = location.state?.category || categorySlug || null;
    const subcategory = location.state?.subcategory || subcategorySlug || null;
    const [products, setProducts] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [quickViewProduct, setQuickViewProduct] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false); // mobile filter modal state
    const [tempMobileFilters, setTempMobileFilters] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});
    const [selected, setSelected] = useState("");
    const [showShort, setShowShort] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = Number(searchParams.get("page")) || 1;
    const { data, filters, sorting, pagination, loading } = useSelector((state) => state.products);
    const [dataReady, setDataReady] = useState(true);
    const [filtersInitialized, setFiltersInitialized] = useState(false);

    const { total, totalPages, rangeStart, rangeEnd } = getPaginationMeta(pagination, currentPage);
    const filterTitle = capitalizeWordsDash(subcategory || category);
    const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

    // ========================= EFFECTS =========================
    useEffect(() => {

        if (!hasFetchedOnce) return;

        if (loading) {
            setDataReady(true);
            return;
        }

        if (Array.isArray(data)) {
            setProducts(data);
            setDataReady(false);
        }
    }, [data, loading, hasFetchedOnce]);




    useEffect(() => {
        document.title = "Obsessions - Products";

        const token = localStorage.getItem("token");
        setIsLoggedIn(Boolean(token));

        const urlFilters = getFiltersFromURL(location.search);

        setSelectedFilters(urlFilters);
        setTempMobileFilters(urlFilters);

        setFiltersInitialized(true);
    }, [location.search]);



    useEffect(() => {
        if (!category) return;

        setDataReady(true);

        dispatch(
            fetchProducts({
                category,
                subcategory,
                page: currentPage,
                limit: 40,
                filters: selectedFilters || {},
            })
        )
            .unwrap()
            .finally(() => {
                setHasFetchedOnce(true);
            });

    }, [category, subcategory, selectedFilters, currentPage, dispatch]);


    useEffect(() => {
        if (isFilterOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
    }, [isFilterOpen]);

    // ========================= FILTER HANDLERS =========================
    const getFiltersFromURL = (search) => {
        const params = new URLSearchParams(search);
        const filters = {};

        for (const [key, value] of params.entries()) {
            if (key === "page" || key === "sort") continue;

            filters[key] = value.includes(",")
                ? value.split(",").map((v) => v.trim())
                : [value.trim()];
        }

        return filters;
    };

    const handleFilterChange = (filterKey, filterValue) => {
        setSelectedFilters((prev) => {
            const current = prev[filterKey] || [];

            const updated = current.includes(filterValue)
                ? current.filter((v) => v !== filterValue)
                : [...current, filterValue];

            const newFilters = { ...prev, [filterKey]: updated };

            return newFilters;
        });
    };

    const handleMobileFilterChange = (filterKey, value) => {
        setTempMobileFilters((prev) => {
            const current = prev[filterKey] || [];
            const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
            return { ...prev, [filterKey]: updated };
        });
    };

    const applyMobileFilters = () => {
        setSelectedFilters(tempMobileFilters);
        setIsFilterOpen(false);
        scrollToTop();
    };

    const clearMobileFilters = () => {
        setTempMobileFilters({});
    };

    useEffect(() => {
        if (!filtersInitialized) return;

        const params = new URLSearchParams();

        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0) {
                params.set(key, values.join(","));
            }
        });

        if (currentPage > 1) {
            params.set("page", currentPage);
        }

        const newSearch = params.toString();

        if (newSearch !== location.search.replace("?", "")) {
            navigate(`${location.pathname}?${newSearch}`, { replace: true });
        }

    }, [selectedFilters, currentPage, filtersInitialized]);

    // ========================= WISHLIST =========================
    const toggleWishlist = async (e, product) => {
        toast.dismiss();
        e.stopPropagation();

        if (!isLoggedIn) {
            navigate("/login");
            return;
        }

        const isInWishlist = product.is_wishlisted;
        try {
            if (isInWishlist) {
                const wishlistItem = product.wishlist[0]?.wishlist_id;
                if (wishlistItem) {
                    await dispatch(removeFromWishlist(wishlistItem)).unwrap();
                    // toast.success("Removed from wishlist", { autoClose: 1500 });
                    getCartWishlistCount();
                    setProducts((prev) =>
                        prev.map((p) => (p.id === product.id ? { ...p, is_wishlisted: false, wishlist: [] } : p))
                    );
                }
            } else {
                const addedWishlistItem = await dispatch(addToWishlist({ product_id: product.id })).unwrap();
                // toast.success("Added to wishlist", { autoClose: 1500 });
                getCartWishlistCount();

                const wishlist = Array.isArray(addedWishlistItem)
                    ? addedWishlistItem.find((w) => w.product_id === product.id)
                    : addedWishlistItem;

                setProducts((prev) =>
                    prev.map((p) =>
                        p.id === product.id ? { ...p, is_wishlisted: true, wishlist: wishlist ? [{ wishlist_id: wishlist.id }] : [] } : p
                    )
                );
            }
        } catch {
            toast.error("Something went wrong");
        }
    };

    // ========================= UTILS =========================
    const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

    const handlePageChange = (page) => {
        const nextPage = Math.min(Math.max(page, 1), totalPages);
        const params = Object.fromEntries(searchParams);
        params.page = nextPage;
        setSearchParams(params);
        scrollToTop();
    };

    const handleSelect = (option, key) => {
        setSelected(option);
        setShowShort(key);
        setSelectedFilters((prev) => ({ ...prev, sort_by: [option] }));
    };

    const hasActiveFilters = Object.values(selectedFilters).some((value) => Array.isArray(value) && value.length > 0);

    // ========================= FILTER RENDERERS =========================

    const renderFilterGroup = (title, options, key, isMobile = false) => {
        const isExpanded = expandedGroups[key] || false;

        const masterOptions = orderByString(options);
        const visibleOptions = isExpanded ? masterOptions : masterOptions?.slice(0, 5);
        const hiddenCount = masterOptions.length - visibleOptions.length;
        const currentFilters = isMobile ? tempMobileFilters : selectedFilters;
        const onChangeHandler = isMobile ? handleMobileFilterChange : handleFilterChange;


        return (
            <div className="custom-filter-group" key={key}>
                <h4>{title}</h4>
                {visibleOptions.map((opt, i) => (
                    <label key={i}>
                        <input
                            type="checkbox"
                            checked={currentFilters[key]?.includes(opt) || false}
                            onChange={() => { scrollToTop(); onChangeHandler(key, opt); }}
                        />
                        <span className="txt_checkbox">{capitalizeWords(opt.trim())}</span>
                    </label>
                ))}
                {options.length > 5 && (
                    <div className="show-more-row" onClick={() => setExpandedGroups((prev) => ({ ...prev, [key]: !isExpanded }))}>
                        {isExpanded ? "− Show Less" : `+ Show More (${hiddenCount})`}
                    </div>
                )}
            </div>
        );
    };

    const renderCategoryFilter = (categories, isMobile = false) => {
        const groupKey = "categories";

        const currentFilters = isMobile ? tempMobileFilters : selectedFilters;
        const isExpanded = expandedGroups[groupKey] || false;
        const onChangeHandler = isMobile ? handleMobileFilterChange : handleFilterChange;

        // Sort first
        const sortedSubcategories = orderByField(categories.subcategories ?? [], "name");
        // Handle show more / less
        const visibleSubcategories = isExpanded
            ? sortedSubcategories
            : sortedSubcategories.slice(0, 5);

        const hiddenCount = sortedSubcategories.length - 5;

        return (
            <div className="custom-filter-group" key={groupKey}>
                <h4>Categories</h4>

                <div className="subcategory-list">
                    {visibleSubcategories.map((sub) => (
                        <label key={sub.id}>
                            <input
                                type="checkbox"
                                value={sub.name}
                                checked={
                                    currentFilters.categories?.length
                                        ? currentFilters.categories.includes(sub.name)
                                        : sub.name === capitalizeWordsRemoveDash(subcategory)
                                }
                                onChange={() => {
                                    scrollToTop();
                                    onChangeHandler("categories", sub.name);
                                }}
                            />
                            <span className="txt_checkbox">{capitalizeWords(sub.name)}</span>
                        </label>
                    ))}
                </div>

                {sortedSubcategories.length > 5 && (
                    <div
                        className="show-more-row"
                        onClick={() =>
                            setExpandedGroups((prev) => ({
                                ...prev,
                                [groupKey]: !isExpanded,
                            }))
                        }
                    >
                        {isExpanded
                            ? "− Show Less"
                            : `+ Show More (${hiddenCount})`}
                    </div>
                )}
            </div>
        );
    };

    const renderPriceFilter = (priceFilter, isMobile = false) => {
        const currentFilters = isMobile ? tempMobileFilters : selectedFilters;
        const onChangeHandler = isMobile ? handleMobileFilterChange : handleFilterChange;
        return (
            <div className="custom-filter-group" key="price_filter">
                <h4>Price Range</h4>
                {priceFilter.map((price, i) => (
                    <label key={i}>
                        <input
                            type="checkbox"
                            value={price.range_lebel}
                            checked={currentFilters.price_filter?.includes(price.filter_value) || false}
                            onChange={() => { scrollToTop(); onChangeHandler("price_filter", price.filter_value); }}
                        />
                        <span className="txt_checkbox">{price.range_lebel}</span>
                    </label>
                ))}
            </div>
        );
    };

    const renderDiscountFilter = (discounts, isMobile = false) => {
        if (!discounts || discounts.length === 0) return null;
        const currentFilters = isMobile ? tempMobileFilters : selectedFilters;
        const onChangeHandler = isMobile ? handleMobileFilterChange : handleFilterChange;
        return (
            <div className="custom-filter-group" key="discount_filter">
                <h4>Discount</h4>
                {discounts.map((disc, i) => (
                    <label key={i}>
                        <input
                            type="checkbox"
                            value={disc.total_items}
                            checked={currentFilters.discount_filter?.includes(disc.filter_value) || false}
                            onChange={() => { scrollToTop(); onChangeHandler("discount_filter", disc.filter_value); }}
                        />
                        <span className="txt_checkbox">{`${disc.discount_range}`}</span>
                    </label>
                ))}
            </div>
        );
    };

    return (
        <>
            <ToastContainer position="top-right" style={{ zIndex: 9999999999999 }} autoClose={2000} limit={1} hideProgressBar={true} transition={Slide} newestOnTop={true} />
            <div style={{ position: "relative", right: "6px" }}>
                <ProductBreadcrumbs />
            </div>
            {/* MOBILE FILTER BUTTON */}
            {products.length > 0 &&
                <div className="track_filter">
                    {/* <div className="title_hader_filter"> <h2 className="title_prd_roots">
                        {dataReady ? (
                            <Skeleton height={28} width={180} style={{ marginBottom: 10 }} />
                        ) : subcategory ? (
                            capitalizeWords(subcategory)
                        ) : (
                            capitalizeWords(category)
                        )}
                    </h2>
                    </div> */}

                    <div className="root_btn_filter_hader">

                        <div className="mobile-filter-btn" onClick={() => setIsFilterOpen(true)}>
                            <i className="bi bi-sliders"></i> FILTERS
                        </div>

                        <MobileFilter
                            isOpen={isFilterOpen}
                            onClose={() => setIsFilterOpen(false)}
                            filters={filters}
                            applyFilters={applyMobileFilters}
                            clearFilters={clearMobileFilters}
                            renderCategoryFilter={renderCategoryFilter}
                            renderPriceFilter={renderPriceFilter}
                            renderDiscountFilter={renderDiscountFilter}
                            renderFilterGroup={renderFilterGroup}
                            tempFilters={tempMobileFilters}
                            setTempFilters={setTempMobileFilters}
                        />

                        {/* shop by */}
                        <div className="sortby-container">
                            <div className="dropdown">
                                <div
                                    className="dropdown-toggle sortby-btn"
                                    id="dropdownMenuButton"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    <i className="bi bi-sort-down"></i> Sort By
                                </div>
                                <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                    {Object.entries(sorting).map(([key, label]) => (
                                        <li key={key}>
                                            <button
                                                className={`dropdown-item ${selected === key ? "active-option" : ""}`}
                                                onClick={() => handleSelect(key, label)}
                                            >
                                                {label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            }
            <div className="custom-products-page">
                <aside className="custom-filters filter-section-fixed">
                    {products.length > 0 &&
                        <>

                            <div className="root_devider_flt">
                                {dataReady ? (<Skeleton height={22} width={80} style={{ marginBottom: 5 }} />) : (
                                    <>
                                        <h2>Filters</h2>
                                        {hasActiveFilters && (
                                            <p
                                                className="clr-all"
                                                onClick={() => {
                                                    setSelectedFilters({});
                                                    setTempMobileFilters({});
                                                }}
                                            >
                                                Clear all
                                            </p>
                                        )}

                                    </>
                                )}
                            </div>
                        </>
                    }
                    {dataReady ? (<SkeletonFilter />) : filters && Object.keys(filters).length > 0 ? (
                        //  Render filters when available
                        <>
                            {/* 1️ Category Filter */}
                            {filters.categories && renderCategoryFilter(filters.categories)}
                            {/* 2️ Price Filter */}
                            {filters.price_filter && renderPriceFilter(filters.price_filter)}
                            {/* 3️ Discount Filter */}
                            {filters.discount_filter && renderDiscountFilter(filters.discount_filter)}
                            {/* 4️ Product Filters */}
                            {filters.product_filter &&
                                Object.entries(filters.product_filter)
                                    .filter(([key, values]) => Array.isArray(values) && values.length > 0)
                                    .map(([key, values]) =>
                                        renderFilterGroup(key.replace(/_/g, " "), values, key)
                                    )}
                        </>
                    ) : (  //  No filters found            
                        <p className="no-filters">No filters.</p>
                    )}
                </aside>
                <main className="custom-product-list">
                    {/* SORT BAR */}
                    {products.length > 0 &&
                        <>
                            <div className="sortby-container-mlb">
                                <div>
                                    {dataReady ? (
                                        <Skeleton height={28} width={180} style={{ marginBottom: 10 }} />
                                    ) : (
                                        <span className="track_contuing product-heading">
                                            <span className="product-title" style={{ fontSize: "20px" }}>
                                                {/* {filterTitle} */}
                                            </span>
                                            &nbsp;&nbsp;
                                            <span className="text-muted product-range" style={{ fontSize: "14px" }}>
                                                {`Showing ${total} items`}
                                                {/* {`(Showing ${rangeStart}-${rangeEnd} products of ${total} products)`} */}
                                            </span>
                                        </span>
                                    )}
                                </div>
                                <div className="dropdown" style={{ display: "flex", gap: "10px" }}>
                                    <div
                                        className="dropdown-toggle sortby-btn"
                                        id="dropdownMenuButton"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                    >
                                        Sort By &nbsp;
                                        <div className="sort-name">
                                            {showShort}
                                        </div>
                                    </div>
                                    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                                        {Object.entries(sorting).map(([key, label]) => (
                                            <li key={key}>
                                                <button
                                                    className={`dropdown-item ${selected === key ? "active-option" : ""}`}
                                                    onClick={() => handleSelect(key, label)}
                                                >
                                                    {label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </>
                    }
                    <div className="mb-6 mt-1">
                        {dataReady ? (
                            /* LOADING */
                            <SkeletonProduct />
                        ) : products?.length > 0 ? (
                            /* PRODUCTS */
                            <div className="product-grid">
                                {products.map((item, index) => {
                                    const isWishlisted = item.is_wishlisted;

                                    return (
                                        <div
                                            key={index}
                                            className="product-card-dtl pointer-crusser"
                                            style={{ cursor: "pointer" }}
                                        >
                                            <div
                                                className="product-img-box product-img-box001"
                                                onClick={() =>
                                                    window.open(
                                                        getProductPdpUrl(item.action_url, item.sku),
                                                        "_blank",
                                                        "noopener,noreferrer"
                                                    )
                                                }
                                            >
                                                <img
                                                    src={item.media_list?.main?.file}
                                                    alt={item.name}
                                                    title={item.name}
                                                    className="main_image"
                                                    wrapperClassName="product-image"
                                                />
                                                <img
                                                    src={item.media_list?.hover?.file}
                                                    alt={item.name}
                                                    title={item.name}
                                                    className="hover_image"
                                                />

                                                {/* Wishlist Button */}
                                                <button
                                                    className="wishlist-btn_products pointer-crusser"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleWishlist(e, item);
                                                    }}
                                                >
                                                    <Heart
                                                        size={20}
                                                        strokeWidth={2}
                                                        color={isWishlisted ? "#FF0000" : "#000"}
                                                        fill={isWishlisted ? "#FF0000" : "none"}
                                                    />
                                                </button>

                                                {/* Quick View */}
                                                <div className="qucick_dv">
                                                    <span
                                                        className="quick-view_pd"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setQuickViewProduct(item);
                                                            setShowModal(true);
                                                        }}
                                                    >
                                                        Quick View&nbsp;
                                                        <Expand color="#000000" size={15} strokeWidth={1.25} />
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Product Title */}
                                            <p className="product-title pointer-crusser">
                                                <Link
                                                    to={getProductPdpUrl(item.action_url, item.sku)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {limitChars(item.name, 26)}
                                                </Link>
                                            </p>

                                            {/* Product Price */}
                                            <Link
                                                to={getProductPdpUrl(item.action_url, item.sku)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <div className="product-price">
                                                    <span>{formatPrice(item.selling_price)}</span>

                                                    {item.discount_percent > 0 && (
                                                        <>
                                                            <span className="original">
                                                                {formatPrice(item.mrp)}
                                                            </span>
                                                            <span
                                                                className="discount"
                                                                style={{ fontSize: "14px", fontWeight: "500" }}
                                                            >
                                                                {formatDiscount(item.discount_percent)}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </Link>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (<EmptyProducts emptyproduct={emptyproduct} />)}
                    </div>
                    {/* PAGINATION */}
                    <div className="pagination_track">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            totalitems={products.length}
                        />
                    </div>
                </main>
                <ProductQuickViewModal show={showModal} onHide={() => setShowModal(false)} product={quickViewProduct} />
            </div>

            {/* login modal */}
            {showLoginPrompt && (
                <LoginPromptModal onClose={() => setShowLoginPrompt(false)} />
            )}
            {!dataReady && (
                <>
                    <TopPickSection />
                    <CustomerFavourite />
                    <Footer />
                </>
            )}
        </>
    );
};

export default ProductsPage;

